// RELATIVE, not `@/…`: three cb-api suites import this module by path (`jobTrigger`,
// `SCHEDULE_LIST_CAP`), and cb-api's vitest config has no `@` alias — an aliased import here
// fails those suites at collection, and cb-api's `npm test` gates both deploys.
import { PENDO_IGNORE } from '../../../analytics/domPrivacy.js'

/**
 * What a queue job IS — its name and where it came from (spec 132).
 *
 * Pure, no React. One definition, shared by the list (`useReportQueue`'s `named`
 * memo) and the drawer's opener (`ReportQueue`'s `openJob`), because those two used
 * to disagree: only jobs that came out of the LIST were ever named, so a run opened
 * from outside the loaded 50 — every Schedules-page "Last run" link, measured 74 of
 * 74 scheduled runs — was headed "Report 190", printed "Report 34" on its PDF
 * masthead and saved as `report.pdf`.
 *
 * ## The title is the CATALOG REPORT NAME (Jordan, spec 132 decision 1)
 *
 * Never the schedule's name. Live schedule names include "CC-53438" and "this is my
 * weekly report on vendors"; a Balance Sheet PDF titled like that no longer says what
 * the document is. The schedule name is carried separately (`scheduleName`) and shown
 * only as the Trigger note, "from <schedule name>".
 */

/**
 * The map every "no names yet" caller shares. A module constant, so an unresolved
 * read hands out the SAME reference on every render — a fresh `new Map()` there would
 * rebuild every memo keyed on it, and the drawer's focus capture with them (WCAG 2.4.3;
 * see the note on the drawer's focus effect).
 */
export const EMPTY_MAP = new Map()

/** report id (string) → catalog name, from `reportService.list()`'s tabs array. */
export function reportNameMap(tabs) {
  if (!Array.isArray(tabs) || tabs.length === 0) return EMPTY_MAP
  const m = new Map()
  for (const t of tabs) {
    for (const r of t?.reports || []) if (r?.id != null && r.name) m.set(String(r.id), r.name)
  }
  return m
}

/** Spec 192 — a job's reportId for an Exago ("Advanced") report: `exago/<content id>`. */
export const isExagoReportId = (reportId) => String(reportId ?? '').toLowerCase().startsWith('exago/')

/*
 * Spec 216 — an Advanced (Exago) report's id and name, as a person may see them.
 *
 * Its id is `exago/<guid>`: an internal key, not a number to find a report by, so no queue
 * surface prints it ("Report #exago/…" read as noise, and it was the only thing some rows
 * said about the report). A generic Advanced Report's name is FIRM-AUTHORED — it can name a
 * client or a matter — so its page heading, catalog card and breadcrumb carry PENDO_IGNORE
 * (spec 209 §3), and here every element that renders it does too. A queue job or schedule
 * carries no `reviewed` flag, so EVERY `exago/` id is treated that way here, the reviewed
 * report (spec 190) included, although its own card and heading are not masked (its name
 * is ours): masking it errs safe. A schedule made from the Advanced Report page is named
 * after the report by default (`SpecializedFilterBar` `queue.name`), so an Exago schedule's
 * own name is masked too.
 *
 * Every other report id and name renders exactly as before: these return the same string,
 * and add no class, for anything that is not an `exago/` id.
 */
export const EXAGO_GENERIC_NAME = 'Advanced Report'

/** "#N" for a report id a person may see, or null for none or an Advanced (Exago) id. */
export const reportRef = (reportId) =>
  (reportId == null || reportId === '' || isExagoReportId(reportId) ? null : `#${reportId}`)

/**
 * What stands in for an unnamed schedule's name: "Report #N" ("#N" with `word: false`), or
 * "Advanced Report" for an Exago one.
 */
export const unnamedReportLabel = (reportId, { word = true } = {}) =>
  (isExagoReportId(reportId) ? EXAGO_GENERIC_NAME : `${word ? 'Report ' : ''}#${reportId}`)

/** The class an element showing this report's name (or its schedule's) carries: PENDO_IGNORE for Exago. */
export const firmTextClass = (reportId) => (isExagoReportId(reportId) ? PENDO_IGNORE : '')

/**
 * Spec 192 — `catalog` plus the firm's Exago reports, keyed as their jobs are
 * (`exago/<lowercase id>`), from `exagoReportService.list()`. The same `catalog`
 * reference when there is nothing to add, so memos keyed on it stay stable.
 */
/**
 * The name a job's report goes to analytics under (spec 209 §3). An Advanced (Exago)
 * report's name is firm-authored — it can name a client or a matter — so every `exago/`
 * job is "Advanced Report" there; `reportId` still tells them apart. Display is unaffected.
 */
export const ANALYTICS_EXAGO_NAME = 'Advanced Report'
export const analyticsReportName = (job) =>
  (String(job?.reportId ?? '').startsWith('exago/') ? ANALYTICS_EXAGO_NAME : (job?.reportName ?? undefined))

export function withExagoNames(catalog, exagoReports) {
  if (!Array.isArray(exagoReports) || exagoReports.length === 0) return catalog
  const m = new Map(catalog)
  for (const r of exagoReports) if (r?.id && r.name) m.set(`exago/${String(r.id).toLowerCase()}`, r.name)
  return m
}

/**
 * schedule id (string) → schedule name (or null), from the owner's schedule list.
 *
 * EVERY id is a key, named or not, because the map also answers "does this schedule
 * still exist?" (`nameJob`'s `scheduleDeleted`), and an unnamed schedule is not a
 * deleted one. For the same reason its `size` is the read's row count, which is how
 * `nameJob` tells a whole list from one cut at `SCHEDULE_LIST_CAP`.
 */
export function scheduleNameMap(schedules) {
  if (!Array.isArray(schedules) || schedules.length === 0) return EMPTY_MAP
  const m = new Map()
  for (const s of schedules) if (s?.id != null) m.set(String(s.id), s.name || null)
  return m
}

/**
 * schedule id (string) → its "Keep results — last N" count, from the owner's schedule
 * list (spec 152). Only schedules that carry a usable count are keys; a run whose
 * schedule is absent here is told "most recent runs", never a number we do not have.
 * Kept apart from `scheduleNameMap` on purpose: that map's KEYS answer "does this
 * schedule still exist?", and a count missing from a row is not a deleted schedule.
 */
export function scheduleRetainMap(schedules) {
  if (!Array.isArray(schedules) || schedules.length === 0) return EMPTY_MAP
  const m = new Map()
  for (const s of schedules) {
    const n = Number(s?.retainCount)
    if (s?.id != null && Number.isInteger(n) && n > 0) m.set(String(s.id), n)
  }
  return m.size ? m : EMPTY_MAP
}

/**
 * The ONE fallback chain: catalog name → `Report {id}` → action key → `Job {id8}`.
 * Replaces a 6-character copy in the list and an 8-character copy in the drawer.
 *
 * `Report {id}` comes BEFORE the action key on purpose (spec 132 decision 4: while
 * names are unresolved a download is titled "Report {id}" and saved as an id-bearing
 * `report-34.pdf`). The list's old chain preferred the action key, which was harmless
 * while no detail carried one; now that the detail does, the old order would title an
 * unresolved Balance Sheet PDF "balance-sheet" — internal vocabulary on a masthead,
 * with no id to find the report by. The action key names only a job with no report id.
 */
export function jobDisplayName(job, reportNames = EMPTY_MAP) {
  if (!job) return ''
  return reportNames.get(String(job.reportId))
    // Spec 192: an Exago run's id (`exago/<guid>`) is no number to find a report by.
    || (isExagoReportId(job.reportId) ? EXAGO_GENERIC_NAME : null)
    || (job.reportId ? `Report ${job.reportId}` : null)
    || job.actionKey
    || `Job ${String(job.id).slice(0, 8)}`
}

/**
 * The most rows cb-api's `GET /reports/schedules` returns, with no cursor to ask for
 * more (`schedules.js`: `listSchedules(db, …, 100, …)`, which is `ORDER BY enabled
 * DESC, next_run_at ASC LIMIT ?`). A list this long may be CUT SHORT — and the rows
 * cut are the owner's disabled and far-future schedules — so absence from it proves
 * nothing. Pinned against the route in `cb-api/tests/schedule-provenance-invariants.test.js`.
 */
export const SCHEDULE_LIST_CAP = 100

/**
 * How long before the schedules read a run must have been created for its absence
 * from that read to count. `createdAt` is the WORKER's clock; the read time is THIS
 * BROWSER's (react-query's `dataUpdatedAt`). A browser clock running ahead would
 * otherwise make a run created just AFTER the read — a schedule made in another tab
 * and Run now — look older than the read, and tell a live schedule's run that its
 * schedule was deleted. Five minutes costs little: the run this wording exists for is
 * an orphan left behind by a deleted schedule, and the one measured (aed5dda6) was six
 * weeks old. A younger orphan keeps the ordinary wording, which is where it started.
 */
export const SCHEDULE_READ_SKEW_MS = 5 * 60_000

/**
 * The job, named. Idempotent — naming a named job gives the same answer.
 *
 * - `displayName` — `jobDisplayName` above. Never the schedule name.
 * - `reportName`  — the catalog name or null. Analytics reads this, so a fallback
 *                   string ("Report 34") never enters an export series as a name.
 * - `scheduleName`— the name of the schedule that produced it, or null.
 * - `scheduleDeleted` — `true` ONLY when it is provable (below). `false` when the
 *                   schedule is in the list — presence proves existence whatever
 *                   else is true of the read. `null` when nothing can be said.
 *
 * ## When absence from the schedule list proves deletion
 *
 * All of these, or `scheduleDeleted` is null and the drawer keeps its ordinary
 * wording:
 *
 *  1. The run is OWNED and names a schedule. An owned run's schedule is the owner's
 *     schedule — both `fireScheduleNow` and the cron create the job as the schedule's
 *     owner — so the owner's own list is the list to look in.
 *  2. `schedulesResolved` — a completed read of the caller's OWN schedules (state
 *     'ready', scope 'mine'). Absence from a read that did not happen is not evidence.
 *  3. The read was not cut short: fewer than `SCHEDULE_LIST_CAP` rows.
 *  4. The run is OLDER than the read, by `SCHEDULE_READ_SKEW_MS`. A resolved list is
 *     only as complete as the moment it was read: the Queue page reads schedules on
 *     mount and does not poll them, while it polls jobs every 5 s, so a schedule
 *     created afterwards (another tab, another device) is missing from the list while
 *     its runs are not. A run created before the read had a schedule that existed
 *     before the read, so the read would have seen it — unless it was deleted.
 *
 * `schedulesReadAt` is that read's time (epoch ms), passed only with a resolved read.
 * Without it, condition 4 cannot be checked and nothing is inferred.
 *
 * - `scheduleRetainCount` (spec 152) — the schedule's "last N", from `scheduleRetains`
 *                   (`scheduleRetainMap`), or null. The retention sentence says "last N
 *                   runs" only with it, and "most recent runs" without.
 */
export function nameJob(job, { reportNames = EMPTY_MAP, scheduleNames = EMPTY_MAP, scheduleRetains = EMPTY_MAP, schedulesResolved = false, schedulesReadAt = null } = {}) {
  if (!job) return job
  const scheduleKey = job.scheduleId != null ? String(job.scheduleId) : null
  const owned = job.isOwner !== false
  let scheduleDeleted = null
  if (scheduleKey && owned && schedulesResolved) {
    if (scheduleNames.has(scheduleKey)) scheduleDeleted = false
    else if (readProvesAbsence(job, scheduleNames, schedulesReadAt)) scheduleDeleted = true
  }
  return {
    ...job,
    displayName: jobDisplayName(job, reportNames),
    reportName: reportNames.get(String(job.reportId)) ?? null,
    scheduleName: (scheduleKey ? scheduleNames.get(scheduleKey) : null) ?? job.scheduleName ?? null,
    scheduleDeleted,
    scheduleRetainCount: (scheduleKey ? scheduleRetains.get(scheduleKey) : null) ?? job.scheduleRetainCount ?? null,
  }
}

/** Conditions 3 and 4 of `nameJob`'s note: the read was whole, and it post-dates the run. */
function readProvesAbsence(job, scheduleNames, readAt) {
  return scheduleNames.size < SCHEDULE_LIST_CAP
    && Number.isFinite(readAt)
    && Number.isFinite(job.createdAt)
    && job.createdAt < readAt - SCHEDULE_READ_SKEW_MS
}

/**
 * Where a run came from — the drawer's Trigger fact, the expired panel's retention
 * sentence and the queue row's recurring glyph all read this one answer.
 *
 * Returns `{ key, label, note, fromSchedule }`:
 *
 *   key        label                   when
 *   ---------  ----------------------  ---------------------------------------------
 *   cron       Ran on a schedule       scheduleId set, scheduledFor <  createdAt
 *   run-now    Started with Run now    scheduleId set, scheduledFor == createdAt and
 *                                      NOT on a minute boundary
 *   schedule   From a schedule         scheduleId set, and the data cannot tell which
 *                                      (equality ON a minute boundary, or no
 *                                      scheduledFor) — makes no claim
 *   manual     Queued manually         scheduleId known to be null (`scheduleKnown`)
 *   unknown    Not recorded            the payload carried no schedule key at all (an
 *                                      older worker's detail)
 *
 * A non-null `scheduleId` is a schedule run WHATEVER `scheduleKnown` says — only a
 * null one needs the flag, to tell "no schedule" from "the payload didn't say".
 *
 * `note` is "from <schedule name>" for every schedule label when the name is known.
 *
 * ## The cb-api invariants this depends on — each pinned in
 * `cb-api/tests/schedule-provenance-invariants.test.js`
 *
 *  - Run now: `fireScheduleNow` writes `scheduled_for = now`, the same instant as the
 *    job's `created_at` (cb-api `jobs/runner.js`, the `UPDATE jobs SET schedule_id=?,
 *    scheduled_for=?` in fireScheduleNow). A wall-clock ms is almost never a whole
 *    minute (0 of 14 live Run-now rows were).
 *  - Cron: `runDueSchedules` writes `scheduled_for = s.next_run_at` (same UPDATE, in
 *    runDueSchedules). Its `?? now` fallback is unreachable, because
 *    `listDueSchedules` requires `next_run_at IS NOT NULL` (`jobs/schedulesStore.js`).
 *  - `next_run_at` always comes from `nextRunAt`, built from `{hour, minute}`
 *    (`jobs/recurrence.js`, the `zonedTimeToUtc` call), so it is a whole minute
 *    (60 of 60 live cron rows were).
 *
 * Equality alone is NEVER treated as Run now: live cron lags as small as 3 ms and
 * 18 ms were measured, so a 0 ms lag is possible — and on a whole minute it reads
 * "From a schedule", which claims nothing.
 */
export function jobTrigger(job) {
  const note = job?.scheduleName ? `from ${job.scheduleName}` : null
  if (job?.scheduleId != null) {
    const sf = job.scheduledFor
    const ca = job.createdAt
    if (sf != null && ca != null && sf < ca) {
      return { key: 'cron', label: 'Ran on a schedule', note, fromSchedule: true }
    }
    if (sf != null && ca != null && sf === ca && sf % 60_000 !== 0) {
      return { key: 'run-now', label: 'Started with Run now', note, fromSchedule: true }
    }
    return { key: 'schedule', label: 'From a schedule', note, fromSchedule: true }
  }
  if (job?.scheduleKnown === true) return { key: 'manual', label: 'Queued manually', note: null, fromSchedule: false }
  return { key: 'unknown', label: 'Not recorded', note: null, fromSchedule: false }
}
